import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { getFileOpeningById } from '../services/fileOpenings';
import toast from 'react-hot-toast';

// import FileOpeningPdfButton from '../components/pdf/FileOpeningPdfButton';
import { PDFViewer } from '@react-pdf/renderer';
import FileOpeningPdf from '../components/pdf/FileOpeningPdf';
import FileOpeningAllPdfButton from '../components/pdf/FileOpeningAllPdfButton';
import ClientCareGeneralPdf from '../components/pdf/ClientCareGeneralPdf';
import FixedFeePdf from '../components/pdf/FixedFeePdf';

const FileOpeningView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fileOpening, setFileOpening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showFixedFeePdfPreview, setShowFixedFeePdfPreview] = useState(false);
  const [showClientCarePdfPreview, setShowClientCarePdfPreview] = useState(false);

  useEffect(() => {
    const fetchFileOpening = async () => {
      try {
        const data = await getFileOpeningById(id);
        setFileOpening(data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFileOpening();
  }, [id]);

  const renderSection = (title, data) => (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4 border-b pb-1 text-gray-800 dark:text-white">{title}</h2>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex flex-col">
            <span className="font-semibold text-gray-600 dark:text-gray-400">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            <span className="text-gray-800 dark:text-white">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout pageTitle="File Opening Details">
      <div className="flex justify-between mb-4">
        <div>
          <button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
            Back
          </button>
        </div>
        <div>
          {fileOpening && <FileOpeningAllPdfButton data={fileOpening} />}
          <button onClick={() => setShowFixedFeePdfPreview(!showFixedFeePdfPreview)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow">
            {showFixedFeePdfPreview ? 'Hide Fixed Fee Preview' : 'Show Fixed Fee Preview'}
          </button>
          <button onClick={() => setShowClientCarePdfPreview(!showClientCarePdfPreview)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
            {showClientCarePdfPreview ? 'Hide Client Care Preview' : 'Show Client Care Preview'}
          </button>
          <button onClick={() => setShowPdfPreview(!showPdfPreview)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
            {showPdfPreview ? 'Hide PDF Preview' : 'Show PDF Preview'}
          </button>
          <button onClick={() => navigate(`/file-opening/${id}/edit`)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
            Edit
          </button>
        </div>
      </div>
      {showClientCarePdfPreview && fileOpening && (
        <div style={{ height: '80vh' }} className="mb-4">
          <PDFViewer width="100%" height="100%">
            <ClientCareGeneralPdf data={fileOpening} />
          </PDFViewer>
        </div>
      )}
      {showFixedFeePdfPreview && fileOpening && (
        <div style={{ height: '80vh' }} className="mb-4">
          <PDFViewer width="100%" height="100%">
            <FixedFeePdf data={fileOpening} />
          </PDFViewer>
        </div>
      )}
      {showPdfPreview && fileOpening && (
        <div style={{ height: '80vh' }} className="mb-4">
          <PDFViewer width="100%" height="100%">
            <FileOpeningPdf data={fileOpening} />
          </PDFViewer>
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 dark:text-gray-50 shadow-md rounded-lg p-4 sm:p-8">
        {loading ? (
          <p>Loading...</p>
        ) : fileOpening ? (
          <div>
            {renderSection('Case Info', {
              date: fileOpening.date,
              matter_type: fileOpening.matter_type,
              case_details: fileOpening.case_details,
            })}
            {renderSection('Client Details', {
              surname: fileOpening.surname,
              forename: fileOpening.forename,
              title: fileOpening.title,
              dob: fileOpening.dob,
              marital_status: fileOpening.marital_status,
              ni_number: fileOpening.ni_number,
              ethnic_origin: fileOpening.ethnic_origin,
              disability: fileOpening.disability,
              immigration_status: fileOpening.immigration_status,
            })}
            {renderSection('Proceeding Details', {
              venue: fileOpening.venue,
              stage: fileOpening.stage,
              key_date: fileOpening.key_date,
            })}
            {renderSection('Address', {
              number_street: fileOpening.number_street,
              city: fileOpening.city,
              country: fileOpening.country,
              postal_code: fileOpening.postal_code,
              telephone: fileOpening.telephone,
              mobile: fileOpening.mobile,
            })}
            {renderSection('Identification', {
              id_carried_out: fileOpening.id_carried_out,
              evidence_relied_upon: fileOpening.evidence_relied_upon,
              conflict_check: fileOpening.conflict_check,
              funding_source: fileOpening.funding_source,
            })}
            {renderSection('Sponsor Details', {
              sponsor_name: fileOpening.sponsor_name,
              sponsor_number_street: fileOpening.sponsor_number_street,
              sponsor_city: fileOpening.sponsor_city,
              sponsor_country: fileOpening.sponsor_country,
              sponsor_postal_code: fileOpening.sponsor_postal_code,
              sponsor_telephone: fileOpening.sponsor_telephone,
            })}
            {renderSection('Lawyer Costs', {
              fee_earner: fileOpening.fee_earner,
              fixed_fee: fileOpening.fixed_fee,
              fixed_fee_agreed: fileOpening.fixed_fee_agreed,
              hourly_rate: fileOpening.hourly_rate,
              hourly_rate_agreed: fileOpening.hourly_rate_agreed,
              alternative_cost: fileOpening.alternative_cost,
            })}
            {renderSection('Completion', {
              completed_by: fileOpening.completed_by,
              matter_description: fileOpening.matter_description,
            })}
          </div>
        ) : (
          <p>File opening not found.</p>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FileOpeningView;
